<script setup lang="ts">
import { PlusIcon, UsersPlusIcon } from '@gohighlevel/ghl-icons/24/outline'
import { UISelect } from '@gohighlevel/ghl-ui'
import { computed, nextTick, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import AppState from '../../states/app'
import DependentSettings from '../common/DependentSettings.vue'
const { t } = useI18n()

const state = reactive({
  showDependentSettings: false,
  selectedUserIds: [] as string[],
})

const usersInLocationOptions = computed(() => {
  const { users } = AppState.globalUsers as any
  return (
    users?.map((x: any) => ({
      value: x.id,
      label: x.name,
    })) || []
  )
})

const teamMembersChanged = (value: string[]) => {
  state.selectedUserIds = value
}

const onClickConfirm = () => {
  // Create team members array from selected user IDs
  const teamMembers = state.selectedUserIds.map((userId: string) => {
    const user = (AppState.globalUsers as any)?.users?.find(
      (u: any) => u.id === userId
    )
    return {
      userId,
      priority: 0.5,
      selected: true,
      isZoomAdded: 'false',
      zoomOauthId: '',
      meetingLocation: '',
      useMeetingForCollective: false,
      locationConfigurations: [
        {
          kind: 'custom',
          location: '',
          position: 0,
        },
      ],
    }
  })

  AppState.editCalendarValidationData.teamMembers = teamMembers
  state.showDependentSettings = false
}

const onClickCancel = () => {
  state.showDependentSettings = false
  // Reset to current team members
  const currentTeamMembers =
    AppState.editCalendarValidationData.teamMembers || []
  state.selectedUserIds = currentTeamMembers.map((x: any) => x.userId)
}

const addUsers = () => {
  AppState.selectedCalendarSetupStep = ''
  nextTick(() => {
    AppState.selectedCalendarSetupStep = 'staff-location'
  })
}
</script>

<template>
  <div>
    <div class="flex flex-col items-center justify-center gap-2">
      <div
        class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center"
      >
        <UsersPlusIcon class="w-5 h-5 text-gray-600" />
      </div>
      <div class="text-gray-900 font-medium text-[15px]">
        {{ $t('calendar_advanced.availability.no_users_added') }}
      </div>
      <div class="text-gray-600 text-center font-normal text-[13px]">
        {{ $t('calendar_advanced.availability.no_users_added_description') }}
      </div>
      <div>
        <div
          class="flex items-center gap-2 cursor-pointer border border-primary-300 rounded-md py-2 px-4 w-fit mt-2"
          @click="addUsers"
          tabindex="0"
          @keydown="addUsers"
          role="button"
        >
          <PlusIcon class="w-5 h-5 text-primary-700" />
          <div class="text-primary-700">
            {{ $t('calendar_advanced.availability.add_users') }}
          </div>
        </div>
      </div>
    </div>

    <DependentSettings
      v-if="state.showDependentSettings"
      :show-modal="state.showDependentSettings"
      :title="'Add a user to your meeting'"
      @confirm-click="onClickConfirm"
      @cancel-click="onClickCancel"
    >
      <template #body>
        <div class="mb-[20px]">
          <div class="mb-[10px] text-[14px] font-medium text-gray-700">
            {{ t('create_calendar.service_select_team_member') }}:
          </div>
          <UISelect
            id="calendar-select-team-member"
            v-model:value="state.selectedUserIds"
            :options="usersInLocationOptions"
            :filterable="true"
            :multiple="true"
            :placeholder="t('placeholders.please_select')"
            @change="teamMembersChanged"
          />
        </div>
      </template>
    </DependentSettings>
  </div>
</template>